/**
 * LocalStorage 工具函数
 */

/**
 * 获取本地存储的数据
 * @param {string} key - 存储键
 * @param {*} defaultValue - 默认值
 * @returns {*}
 */
export function getLocalStorage(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error(`Error reading localStorage key "${key}":`, error)
    return defaultValue
  }
}

/**
 * 设置本地存储的数据
 * @param {string} key - 存储键
 * @param {*} value - 存储值
 */
export function setLocalStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error)
  }
}

/**
 * 删除本地存储的数据
 * @param {string} key - 存储键
 */
export function removeLocalStorage(key) {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error)
  }
}

/**
 * 获取点赞状态
 * @param {string} toyId - Toy ID
 * @returns {boolean}
 */
export function getLikeStatus(toyId) {
  const likes = getLocalStorage('toy-likes', {})
  return likes[toyId] || false
}

/**
 * 设置点赞状态
 * @param {string} toyId - Toy ID
 * @param {boolean} status - 点赞状态
 */
export function setLikeStatus(toyId, status) {
  const likes = getLocalStorage('toy-likes', {})
  likes[toyId] = status
  setLocalStorage('toy-likes', likes)
}

/**
 * 获取收藏状态
 * @param {string} toyId - Toy ID
 * @returns {boolean}
 */
export function getFavoriteStatus(toyId) {
  const favorites = getLocalStorage('toy-favorites', {})
  return favorites[toyId] || false
}

/**
 * 设置收藏状态
 * @param {string} toyId - Toy ID
 * @param {boolean} status - 收藏状态
 */
export function setFavoriteStatus(toyId, status) {
  const favorites = getLocalStorage('toy-favorites', {})
  favorites[toyId] = status
  setLocalStorage('toy-favorites', favorites)
}

/**
 * 统计数据管理
 */

/**
 * 获取Toy的统计数据
 * @param {string} toyId - Toy ID
 * @returns {Object} 包含 likes, favorites, views 的统计对象
 */
export function getToyStats(toyId) {
  const stats = getLocalStorage('toy-stats', {})
  return {
    likes: stats[toyId]?.likes || 0,
    favorites: stats[toyId]?.favorites || 0,
    views: stats[toyId]?.views || 0
  }
}

/**
 * 增加Toy的点赞数
 * @param {string} toyId - Toy ID
 * @param {number} increment - 增量，默认为1
 */
export function incrementToyLikes(toyId, increment = 1) {
  const stats = getLocalStorage('toy-stats', {})
  if (!stats[toyId]) {
    stats[toyId] = { likes: 0, favorites: 0, views: 0 }
  }
  stats[toyId].likes = (stats[toyId].likes || 0) + increment
  setLocalStorage('toy-stats', stats)
}

/**
 * 增加Toy的收藏数
 * @param {string} toyId - Toy ID
 * @param {number} increment - 增量，默认为1
 */
export function incrementToyFavorites(toyId, increment = 1) {
  const stats = getLocalStorage('toy-stats', {})
  if (!stats[toyId]) {
    stats[toyId] = { likes: 0, favorites: 0, views: 0 }
  }
  stats[toyId].favorites = (stats[toyId].favorites || 0) + increment
  setLocalStorage('toy-stats', stats)
}

/**
 * 增加Toy的观看数
 * @param {string} toyId - Toy ID
 * @param {number} increment - 增量，默认为1
 */
export function incrementToyViews(toyId, increment = 1) {
  const stats = getLocalStorage('toy-stats', {})
  if (!stats[toyId]) {
    stats[toyId] = { likes: 0, favorites: 0, views: 0 }
  }
  stats[toyId].views = (stats[toyId].views || 0) + increment
  setLocalStorage('toy-stats', stats)
}

/**
 * 设置Toy的统计数据
 * @param {string} toyId - Toy ID
 * @param {Object} statsData - 统计数据对象 { likes, favorites, views }
 */
export function setToyStats(toyId, statsData) {
  const stats = getLocalStorage('toy-stats', {})
  stats[toyId] = {
    likes: statsData.likes ?? stats[toyId]?.likes ?? 0,
    favorites: statsData.favorites ?? stats[toyId]?.favorites ?? 0,
    views: statsData.views ?? stats[toyId]?.views ?? 0
  }
  setLocalStorage('toy-stats', stats)
}

/**
 * 获取所有Toy的统计数据
 * @returns {Object} 以toyId为key的统计数据对象
 */
export function getAllToyStats() {
  return getLocalStorage('toy-stats', {})
}
